import { lang } from "../../i18nLang";

export class InvalidValueException extends Error {
  protected _code: string;
  public readonly field: string;
  public readonly valueInformed: string;
  protected _customMessage?: string;
  protected paramsToMessages: any[] = [];

  constructor(valueInformed: any, field?: string, message?: string) {
    super(
      `${message} But received the value ${valueInformed} from field ${field}` ||
        `Invalid value ${valueInformed} received from field ${field}`
    );

    this.name = "InvalidValueException";
    this._code = "invalidValue";
    this.field = field || "";
    this.valueInformed = valueInformed;
  }

  get customMessage() {
    return this._customMessage || this.messages.short;
  }

  get messages() {
    const paramsExtra = this.paramsToMessages.map((value) => String(value));
    return {
      short: translateExceptions(this._code, "", paramsExtra),
      long: translateExceptions(this._code, this.field, paramsExtra),
    };
  }
}

export class GreaterThanException extends InvalidValueException {
  constructor(
    valueInformed,
    valueCompared,
    field?: string,
    customMessage?: string
  ) {
    super(
      valueInformed,
      field,
      `The value must be greater than ${valueCompared}.`
    );

    this.name = "GreaterThanException";
    this._code = "greaterThan";
    this._customMessage = customMessage;
    this.paramsToMessages = [valueCompared];
  }
}

export class GreaterThanOrEqualException extends InvalidValueException {
  constructor(
    valueInformed,
    valueCompared,
    field?: string,
    customMessage?: string
  ) {
    super(
      valueInformed,
      field,
      `The value must be greater than or equal to ${valueCompared}.`
    );

    this.name = "GreaterThanOrEqualException";
    this._code = "greaterThanOrEqual";
    this._customMessage = customMessage;
    this.paramsToMessages = [valueCompared];
  }
}

export class LessThanException extends InvalidValueException {
  constructor(
    valueInformed,
    valueCompared,
    field?: string,
    customMessage?: string
  ) {
    super(
      valueInformed,
      field,
      `The value must be less than ${valueCompared}.`
    );

    lang("exceptions.lessThan", valueCompared);
    this.name = "LessThanException";
    this._code = "lessThan";
    this._customMessage = customMessage;
    this.paramsToMessages = [valueCompared];
  }
}

export class LessThanOrEqualException extends InvalidValueException {
  constructor(
    valueInformed,
    valueCompared,
    field?: string,
    customMessage?: string
  ) {
    super(
      valueInformed,
      field,
      `The value must be less than or equal to ${valueCompared}.`
    );

    lang("exceptions.lessThan", valueCompared);
    this.name = "LessThanOrEqualException";
    this._code = "lessThanOrEqual";
    this._customMessage = customMessage;
    this.paramsToMessages = [valueCompared];
  }
}

export class BetweenValuesException extends InvalidValueException {
  constructor(
    valueInformed,
    minValue,
    maxValue,
    field?: string,
    customMessage?: string
  ) {
    super(
      valueInformed,
      field,
      `The value must be between ${minValue} and ${maxValue}.`
    );

    this.name = "BetweenValuesException";
    this._code = "betweenValues";
    this._customMessage = customMessage;
    this.paramsToMessages = [minValue, maxValue];
  }
}

export class LengthStringException extends InvalidValueException {
  constructor(
    valueInformed,
    equalValue,
    field?: string,
    customMessage?: string
  ) {
    super(
      valueInformed,
      field,
      `The length of text must be equal to ${equalValue}.`
    );

    this.name = "LengthStringException";
    this._code = "lengthString";
    this._customMessage = customMessage;
    this.paramsToMessages = [equalValue];
  }
}

export class MaxAndMinLengthStringException extends InvalidValueException {
  constructor(
    valueInformed,
    minValue,
    maxValue,
    field?: string,
    customMessage?: string
  ) {
    super(
      valueInformed,
      field,
      `The length of text must be between ${minValue} and ${maxValue}.`
    );

    this.name = "MaxAndMinLengthStringException";
    this._code = "maxAndMinLengthString";
    this._customMessage = customMessage;
    this.paramsToMessages = [minValue, maxValue];
  }
}

export class MaxLengthStringException extends InvalidValueException {
  constructor(valueInformed, maxValue, field?: string, customMessage?: string) {
    super(
      valueInformed,
      field,
      `The length of text must be less than ${maxValue}.`
    );

    this.name = "MaxLengthStringException";
    this._code = "maxLengthString";
    this._customMessage = customMessage;
    this.paramsToMessages = [maxValue];
  }
}

export class MinLengthStringException extends InvalidValueException {
  constructor(valueInformed, minValue, field?: string, customMessage?: string) {
    super(
      valueInformed,
      field,
      `The length of text must be greater than ${minValue}.`
    );

    this.name = "MinLengthStringException";
    this._code = "minLengthString";
    this._customMessage = customMessage;
    this.paramsToMessages = [minValue];
  }
}

export class MustBeIntegerException extends InvalidValueException {
  constructor(valueInformed, field?: string, customMessage?: string) {
    super(valueInformed, field, `The value must a be integer.`);

    this.name = "MustBeIntegerException";
    this._code = "mustBeInteger";
    this._customMessage = customMessage;
  }
}

export class OnlyNumbersException extends InvalidValueException {
  constructor(valueInformed, field?: string, customMessage?: string) {
    super(valueInformed, field, `The value must only have numbers.`);

    this.name = "OnlyNumbersException";
    this._code = "onlyNumbers";
    this._customMessage = customMessage;
  }
}

const translateExceptions = (code: string, field: string, params) => {
  if (field) field = `'${lang(`fields.${field}`).replace("fields.", "")}'`;
  return lang(`exceptions.${code}`, field, ...params).trim() + ".";
};
